import React, { useState, useEffect, useMemo } from 'react';
import { Box, Alert, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import type { AuditEntityType } from '@byte-store-control/shared';
import { PageHeader } from '../../components/PageHeader';
import {
  AuditTable,
  AuditFilters,
  ExportButton,
  getDateRangeValues,
  type DateRangeOption,
} from '../../components/AuditHistory';
import { useAuditLogs } from '../../hooks/useAuditLogs';
import { useStoresQuery } from '../../hooks/queries';
import { MobileSubPageHeader } from '../../components/Mobile';
import { DEFAULT_STORE_FUNCTION } from '../../components/StoreManagement/storeDetailTabs';

interface AllActivityPageProps {
  onBack?: () => void;
}

export const AllActivityPage: React.FC<AllActivityPageProps> = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Filter state - default to 'all' to show all activity
  const [dateRange, setDateRange] = useState<DateRangeOption>('all');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [selectedActionType, setSelectedActionType] = useState<AuditEntityType | ''>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Get stores for filter dropdown
  const { data: stores = [] } = useStoresQuery();

  // Calculate actual date range values
  const dateRangeValues = useMemo(() => {
    if (dateRange === 'custom') {
      return {
        startDate: customStartDate ? new Date(customStartDate).toISOString() : undefined,
        endDate: customEndDate ? new Date(customEndDate).toISOString() : undefined,
      };
    }
    return getDateRangeValues(dateRange);
  }, [dateRange, customStartDate, customEndDate]);

  // Fetch audit logs
  const { logs, isLoading, error, loadMore, hasMore, refresh, partial, failedStoreCount } =
    useAuditLogs({
      storeNumber: selectedStore || undefined,
      entityType: selectedActionType || undefined,
      startDate: dateRangeValues.startDate,
      endDate: dateRangeValues.endDate,
      autoFetch: true,
    });

  // Filter logs by search query (client-side filtering for user name)
  const filteredLogs = useMemo(() => {
    if (!searchQuery) return logs;
    const query = searchQuery.toLowerCase();
    return logs.filter(
      (log) =>
        log.userName?.toLowerCase().includes(query) || log.userEmail?.toLowerCase().includes(query)
    );
  }, [logs, searchQuery]);

  // Refresh when filters change
  useEffect(() => {
    refresh();
  }, [selectedStore, selectedActionType, dateRangeValues.startDate, dateRangeValues.endDate]);

  const handleStoreClick = (storeNumber: string) => {
    navigate(`/stores/${storeNumber}/${DEFAULT_STORE_FUNCTION}`);
  };

  const handleCustomDateChange = (start: string, end: string) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/');
    }
  };

  if (isMobile) {
    return (
      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
        <MobileSubPageHeader
          title="Activity Log"
          subtitle="Track all changes"
          onBack={handleBack}
        />

        <Box sx={{ flex: 1, px: 2, pb: 4 }}>
          {/* Filters Card */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              mb: 1.5,
              p: 2,
            }}
          >
            <AuditFilters
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomDateChange={handleCustomDateChange}
              selectedStore={selectedStore}
              onStoreChange={setSelectedStore}
              selectedActionType={selectedActionType}
              onActionTypeChange={setSelectedActionType}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              stores={stores}
              showStoreFilter={true}
              showActionTypeFilter={true}
              showSearchFilter={true}
            />
          </Box>

          {/* Results Card */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            {partial && (
              <Alert severity="warning" sx={{ m: 2 }}>
                Showing partial results — {failedStoreCount} store
                {failedStoreCount === 1 ? '' : 's'} took too long to load. Try narrowing the date
                range or retrying.
              </Alert>
            )}
            <AuditTable
              logs={filteredLogs}
              isLoading={isLoading}
              error={error}
              showStoreColumn={true}
              showUserColumn={true}
              onLoadMore={loadMore}
              hasMore={hasMore}
              onStoreClick={handleStoreClick}
              emptyMessage="No activity found"
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Activity Log"
        description="Track all changes across your stores"
        action={<ExportButton logs={filteredLogs} />}
      />

      <AuditFilters
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        onCustomDateChange={handleCustomDateChange}
        selectedStore={selectedStore}
        onStoreChange={setSelectedStore}
        selectedActionType={selectedActionType}
        onActionTypeChange={setSelectedActionType}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        stores={stores}
        showStoreFilter={true}
        showActionTypeFilter={true}
        showSearchFilter={true}
      />

      {partial && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing partial results — {failedStoreCount} store{failedStoreCount === 1 ? '' : 's'} took
          too long to load. Try narrowing the date range or retrying.
        </Alert>
      )}

      <AuditTable
        logs={filteredLogs}
        isLoading={isLoading}
        error={error}
        showStoreColumn={true}
        showUserColumn={true}
        onLoadMore={loadMore}
        hasMore={hasMore}
        onStoreClick={handleStoreClick}
        emptyMessage="No activity found for the selected filters"
      />
    </Box>
  );
};
